/**
 * How the contracts status card reads each dependency.
 *
 * Pure, so the wording for every combination is pinned by a test: an operator
 * reads this card to decide whether to ring the developer or Cloudflare, and
 * "Indisponibil" must never appear for something that simply is not set yet.
 */

export const TONE = {
  neutral: "bg-[#F3F4F6] text-[#6B7280]",
  warn: "bg-[#FDF3E7] text-[#8a6a1a]",
  ok: "bg-[#EAF4EE] text-[#2F6B4E]",
  bad: "bg-[#FDECEC] text-[#9b1f2c]",
};

/** `{label, tone}` for one checked dependency: configured? reachable? */
export function dependencyBadge({ configured, ok } = {}) {
  if (!configured) return { label: "Neconfigurat", tone: "warn" };
  if (ok) return { label: "Conectat", tone: "ok" };
  return { label: "Indisponibil", tone: "bad" };
}

/** Why a check failed, worded as what to look at. */
export function failureHint(error) {
  if (!error || error === "not_configured") return "";
  if (error === "http_401" || error === "http_403") return "Cheia a fost respinsă. Verifică valoarea din Render.";
  if (error === "AccessDenied" || error === "InvalidAccessKeyId" || error === "SignatureDoesNotMatch") {
    return "Cloudflare a refuzat cheia. Verifică tokenul R2 și bucket-ul la care are acces.";
  }
  if (error === "NoSuchBucket") return "Bucket-ul nu există la această adresă. Verifică R2_BUCKET și jurisdicția EU.";
  return `Eroare: ${error}`;
}

/** A contract's status, as the enrollment drawer shows it. */
export const CONTRACT_STATUS = {
  sending: { label: "Se trimite", tone: "neutral" },
  send_failed: { label: "Netrimis", tone: "bad" },
  sent: { label: "Trimis — așteaptă semnătura participantului", tone: "warn" },
  participant_signed: { label: "Semnat de participant — urmează semnătura FPED", tone: "warn" },
  completed: { label: "Semnat de ambele părți", tone: "ok" },
  rejected: { label: "Refuzat de participant", tone: "bad" },
  rejected_fped: { label: "Refuzat din contul FPED", tone: "bad" },
  canceled: { label: "Anulat", tone: "neutral" },
};

export function contractStatus(status) {
  return CONTRACT_STATUS[status] || { label: status || "—", tone: "neutral" };
}

/** A contract's label, telling apart who refused it. */
export function contractStatusOf(contract) {
  const s = contract?.status;
  return contractStatus(s === "rejected" && contract?.rejected_by === "fped" ? "rejected_fped" : s);
}

/**
 * Which buttons make sense for this contract. Pure, so the rules are tested:
 * a button that eSemneaza would refuse is a button not shown.
 */
export function contractActions(contract) {
  const s = contract?.status;
  const inactive = !contract || !contract.active;
  return {
    send: inactive,
    refresh: Boolean(contract && ["sent", "participant_signed", "completed"].includes(s)),
    signFped: s === "participant_signed",
    remind: s === "sent",
    cancel: s === "sent" || s === "participant_signed",
    download: Boolean(contract?.archived_at),
  };
}
